import { type JSX, useState } from "react";

import type { InvocationResult, Problem } from "../api/catalog";
import { JobProgress } from "./JobProgress";
import { JsonView } from "./JsonView";

interface ResultPanelProps {
  result: InvocationResult;
}

/**
 * The outcome of one call, rendered for a human first.
 *
 * A failure is explained rather than dumped: a plain-language title, the failing field where the
 * Memory Service told us, and the request id to attach to a bug report. The raw exchange stays
 * one click away for developers.
 */
export function ResultPanel({ result }: ResultPanelProps): JSX.Element {
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>{result.ok ? "Result" : "That call failed"}</h2>
        <span className="badge-row">
          <span className={`flag flag--${result.ok ? "on" : "danger"}`}>
            {result.status === 0 ? "no response" : `HTTP ${result.status}`}
          </span>
          <span className="flag flag--off">{result.duration_ms} ms</span>
          {result.profile_used && <span className="flag flag--off">{result.profile_used}</span>}
        </span>
      </header>

      {result.problem ? <ProblemBlock problem={result.problem} /> : null}

      {result.job && <JobProgress callId={result.call_id} job={result.job} />}

      {result.ok && result.status === 204 && (
        <p className="muted">Done. This operation returns no content.</p>
      )}

      {result.ok && result.data !== null && result.data !== undefined && (
        <JsonView value={result.data} />
      )}

      <TechnicalDetails result={result} />
    </section>
  );
}

function ProblemBlock({ problem }: { problem: Problem }): JSX.Element {
  return (
    <div className="notice notice--error">
      <h3>{problem.title}</h3>
      <p>{problem.detail}</p>

      {problem.field_errors.length > 0 && (
        <ul className="field-errors">
          {problem.field_errors.map((error) => (
            <li key={`${error.field_path}:${error.message}`}>
              <code>{error.field_path}</code> {error.message}
            </li>
          ))}
        </ul>
      )}

      {problem.request_id && (
        <p className="muted">
          Attach this to a bug report — request id <code>{problem.request_id}</code>{" "}
          <CopyButton value={problem.request_id} label="Copy id" />
        </p>
      )}
    </div>
  );
}

function TechnicalDetails({ result }: { result: InvocationResult }): JSX.Element {
  return (
    <details className="details">
      <summary>Technical details</summary>

      <div className="details__body">
        <h4>
          Equivalent curl <CopyButton value={result.curl} label="Copy" />
        </h4>
        <pre className="code-block">{result.curl}</pre>

        <h4>Request</h4>
        <p className="muted">
          {result.request.method} {result.request.url}
          {result.idempotency_key && (
            <>
              {" "}
              · idempotency key <code>{result.idempotency_key}</code>
            </>
          )}
        </p>
        <JsonView value={{ headers: result.request.headers, body: result.request.body }} openDepth={1} />

        <h4>Response</h4>
        <JsonView
          value={{
            status: result.response.status,
            headers: result.response.headers,
            body: result.response.body ?? result.response.text,
          }}
          openDepth={1}
        />
      </div>
    </details>
  );
}

function CopyButton({ value, label }: { value: string; label: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className="copy"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? "Copied" : label}
    </button>
  );
}
